
import { Options, Vue } from "vue-class-component";
import InternationalCountryTable from "@/app/ui/views/pricing/cod-retail-configuration/component/components/international-country-table.vue";
import formatInputMoneyWithDecimal from "@/app/infrastructures/misc/common-library/FormatInputMoneyWithDecimal";
import { CodConfigurationController } from "@/app/ui/controllers/CodConfigurationController";
import { CodConfigurationCountryPayload, SubmitCodConfigurationApiRequest } from "@/data/payload/api/CodConfigurationApiRequest";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ModalMessageEntities, Snackbar } from "@/domain/entities/MainApp";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { DetailCodConfigurationRetail, EditInternationalForm } from "@/domain/entities/CodConfiguration";
import DataWrapper from "@/app/ui/components/data-wrapper/index.vue";

@Options({
  components: {
    InternationalCountryTable
  },
  beforeRouteLeave(to: any, from: any, next: any) {
    if ((this as any).isFormChanged) {
      (this as any).pendingRoute = to;
      (this as any).showLeavePageConfirmation = true;
      next(false);
    } else {
      next();
    }
  }
})
class EditInternational extends Vue {
  form = new EditInternationalForm();
  isLoading = true;
  isError = false;
  errorType = "";
  isSaving = false;
  isTableValid = true;
  pristineFormJson = "";
  showLeavePageConfirmation = false;
  pendingRoute: any = null;
  detail = new DetailCodConfigurationRetail()

  async fetchDetail() {
    this.isLoading = true;
    this.isError = false;
    try {
      const res = await CodConfigurationController.getDetailCodConfigurationRetail(
        Number(this.$route.params.id)
      );
      this.detail = res;
      this.form.configId = res.ccoId;
      this.form.configName = res.ccoName;
      this.form.configDescription = res.ccoDescription || "";
      this.form.products = this.toProductArray(res.ccoProductType);
      this.form.countries = res.countries;
      this.form.status = res.ccoStatus === "active";
    } catch (error) {
      this.isError = true;
      this.errorType = parsingErrorResponse(error).type;
    } finally {
      this.isLoading = false;
      await this.$nextTick();
      this.pristineFormJson = this.formJson;
    }
  }

  mounted() {
    this.fetchDetail();
  }

  get formJson(): string {
    return JSON.stringify({
      description: this.form.configDescription,
      products: this.form.products,
      countries: this.form.countries,
      status: this.form.status
    });
  }

  get isFormChanged(): boolean {
    return !!this.pristineFormJson && this.formJson !== this.pristineFormJson;
  }

  get isFormValid(): boolean {
    return (
      !!this.form.configId &&
      this.form.configName.length > 0 &&
      this.form.configDescription.length >= 3 &&
      this.form.products.length >= 1 &&
      this.isTableValid
    );
  }

  toProductArray(value: any): string[] {
    if (Array.isArray(value)) return value;
    const str = String(value || "");
    return str ? str.split(",") : [];
  }

  onChangeStatus() {
    this.form.status = !this.form.status;
  }

  onTableValidityChange(valid: boolean) {
    this.isTableValid = valid;
  }

  submit() {
    this.isSaving = true;
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();
    const countries = this.form.countries.map((country) => (new CodConfigurationCountryPayload({
      countryId: country.countryId,
      minPrice: Number(
        formatInputMoneyWithDecimal(country.minPrice, 2, country.currencyCode, {
          reset: true
        })
      ),
      maxPrice: Number(
        formatInputMoneyWithDecimal(country.maxPrice, 2, country.currencyCode, {
          reset: true
        })
      ),
      minCodFee: Number(
        formatInputMoneyWithDecimal(
          country.minCodFee,
          2,
          country.currencyCode,
          { reset: true }
        )
      ),
      posDeliveryCommissionPercentage: Number(
        formatInputMoneyWithDecimal(
          country.posDeliveryCommissionPercentage,
          2,
          country.currencyCode,
          { reset: true }
        )
      ),
      codPercentage: Number(
        formatInputMoneyWithDecimal(
          country.posDeliveryCommissionPercentage,
          2,
          country.currencyCode,
          { reset: true }
        )
      )
    })));
    const payload = new SubmitCodConfigurationApiRequest({
      configId: this.form.configId,
      configName: this.form.configName,
      configDescription: this.form.configDescription,
      products: this.form.products.join(","),
      status: this.form.status ? "active" : "inactive",
      precentageCodFee: this.detail.ccoPercentageCod,
      countries
    });
    CodConfigurationController.editCodConfiguration(payload)
      .then(() => {
        this.sharedGoBack();
        window.requestAnimationFrame(() => {
          MainAppController.setSnackbarData(
            new Snackbar({
              title: "Berhasil diperbarui",
              type: "announcement",
              message: `Data konfigurasi COD berhasil diperbarui`,
            })
          );
          MainAppController.setSnackbarTimeout(5000);
          MainAppController.setAutoClose(true);
          MainAppController.setSnackbar(true);
        })
      })
      .catch((error: any) => {
        MainAppController.showErrorMessage(
          parsingErrorResponse(error, "Perubahan Gagal !", () => this.submit())
        );
      })
      .finally(() => {
        this.isSaving = false;
        MainAppController.closeLoading();
      });
  }

  confirmLeavePage() {
    this.showLeavePageConfirmation = false;
    this.pristineFormJson = this.formJson;
    if (this.pendingRoute) {
      const route = this.pendingRoute;
      this.pendingRoute = null;
      this.sharedGoBack(route.path || route.fullPath);
    } else {
      this.sharedGoBack();
    }
  }

  sharedGoBack(customPath?: string) {
    this.showLeavePageConfirmation = false;
    this.pristineFormJson = this.formJson;
    this.$router.push(
      customPath || `/tarif/konfigurasi-cod-international/${this.form.configId}`
    );
  }

  goBack() {
    this.showLeavePageConfirmation = false;
    this.$router.back();
  }

  onUpdateStatus() {
    this.form.status = !this.form.status;
  }
}

export default EditInternational;
